import { useCallback, useRef, useState } from 'react';
import { useLivePreviewStore } from '../store/livePreviewStore';

interface ScreenCaptureOptions {
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

export const useScreenCapture = ({ canvasRef }: ScreenCaptureOptions) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const {
    screenshotSettings,
    recordingSettings,
    recordingState,
    startRecording: storeStartRecording,
    stopRecording: storeStopRecording,
    pauseRecording: storePauseRecording,
    resumeRecording: storeResumeRecording,
  } = useLivePreviewStore();

  // Take screenshot
  const takeScreenshot = useCallback(async (): Promise<string> => {
    const canvas = canvasRef.current;
    if (!canvas) return '';

    setIsCapturing(true);

    try {
      // Create a temporary canvas for scaling if needed
      let targetCanvas: HTMLCanvasElement = canvas;

      if (screenshotSettings.scale !== 1) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width * screenshotSettings.scale;
        tempCanvas.height = canvas.height * screenshotSettings.scale;

        const ctx = tempCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);
          targetCanvas = tempCanvas;
        }
      }

      // Convert to data URL
      const mimeType = `image/${screenshotSettings.format}`;
      const quality = screenshotSettings.format === 'png' ? undefined : screenshotSettings.quality;
      const dataUrl = targetCanvas.toDataURL(mimeType, quality);

      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `${screenshotSettings.filename}_${timestamp}.${screenshotSettings.format}`;

      // Trigger download
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return dataUrl;
    } finally {
      setIsCapturing(false);
    }
  }, [canvasRef, screenshotSettings]);

  // Start recording
  const startRecording = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      const stream = canvas.captureStream(recordingSettings.fps);

      // Add audio if requested
      if (recordingSettings.includeAudio) {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          audioStream.getAudioTracks().forEach((track) => {
            stream.addTrack(track);
          });
        } catch (e) {
          console.warn('Could not capture audio:', e);
        }
      }

      // Determine MIME type
      const mimeType = recordingSettings.format === 'gif'
        ? 'image/gif'
        : recordingSettings.format === 'webm'
        ? 'video/webm;codecs=vp9'
        : 'video/mp4';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'video/webm',
        videoBitsPerSecond: recordingSettings.quality === 'high' ? 5000000 :
                           recordingSettings.quality === 'medium' ? 2500000 : 1000000,
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second

      storeStartRecording();
    } catch (e) {
      console.error('Failed to start recording:', e);
    }
  }, [canvasRef, recordingSettings, storeStartRecording]);

  // Stop recording
  const stopRecording = useCallback(async (): Promise<Blob> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(new Blob());
        return;
      }

      mediaRecorder.onstop = () => {
        const mimeType = recordingSettings.format === 'webm'
          ? 'video/webm'
          : 'video/mp4';

        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];

        // Trigger download
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `${recordingSettings.filename}_${timestamp}.${recordingSettings.format}`;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        resolve(blob);
      };

      mediaRecorder.stop();
      storeStopRecording();
    });
  }, [recordingSettings, storeStopRecording]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      storePauseRecording();
    }
  }, [storePauseRecording]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      storeResumeRecording();
    }
  }, [storeResumeRecording]);

  return {
    isCapturing,
    isRecording: recordingState.isRecording,
    isPaused: recordingState.isPaused,
    takeScreenshot,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
  };
};
